import { useState, useRef, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, ChevronDown, Accessibility } from 'lucide-react'
import LanguageSwitcher from '@/components/layout/LanguageSwitcher'
import MobileMenu from '@/components/layout/MobileMenu'
import SearchModal from '@/components/layout/SearchModal'
import { cn } from '@/utils/cn'
import { useCurrentLanguage } from '@/utils/contentVisibility'
import { getVisibleNavRoutes, getVisibleServicesRoutes } from '@/config/routeVisibility'
import { useUiLabels } from '@/hooks/useUiLabels'
import { useLocale } from '@/hooks/useLocale'
import { useBranding } from '@/hooks/useBranding'

export default function Header() {
  const { t } = useTranslation()
  const { getLabel } = useUiLabels()
  const currentLang = useCurrentLanguage()
  const { lang, localizePath } = useLocale()
  const { logoUrl, siteName } = useBranding(lang)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const location = useLocation()
  const servicesRef = useRef<HTMLDivElement>(null)

  const mainNav = useMemo(() => getVisibleNavRoutes(currentLang), [currentLang])
  const servicesNav = useMemo(() => getVisibleServicesRoutes(currentLang), [currentLang])

  const isActive = (path: string) => {
    const localizedPath = localizePath(path)
    if (path === '/') {
      return location.pathname === `/${lang}` || location.pathname === `/${lang}/`
    }
    return location.pathname === localizedPath || location.pathname.startsWith(localizedPath + '/')
  }

  // Tab icon: use the same logo as the header so the site logo is also the tab icon
  useEffect(() => {
    if (!logoUrl) return
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = logoUrl
    link.type = logoUrl.endsWith('.ico') ? 'image/x-icon' : 'image/png'
    let apple = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
    if (!apple) {
      apple = document.createElement('link')
      apple.rel = 'apple-touch-icon'
      document.head.appendChild(apple)
    }
    apple.href = logoUrl
  }, [logoUrl])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setIsServicesOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b-2 border-primary-200 dark:border-primary-900/50 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 3 أجزاء: لوجو | ناٝ | أكشنز - مطابق لتصميم Figma */}
        <div className="flex items-center justify-between gap-6 h-16">
          {/* Logo (per-language from admin branding) */}
          <Link to={localizePath('/')} className="flex items-center space-x-2 space-x-reverse shrink-0" data-tour="site-logo">
            <img
              src={logoUrl}
              alt={t('brand.name', siteName)}
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Navigation (center) – safe links only (US-2.H1) */}
          <nav className="hidden md:flex flex-1 items-center justify-center gap-4" data-tour="main-nav">
            {mainNav.map((item) => (
              <Link
                key={item.path}
                to={localizePath(item.path)}
                className={cn(
                  'flex items-center gap-1.5 transition-colors whitespace-nowrap',
                  isActive(item.path)
                    ? 'text-primary-600 dark:text-primary-400 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                )}
              >
                {item.path === '/quran' ? getLabel(item.labelKey, 'القرآن الكريم') : getLabel(item.labelKey)}
              </Link>
            ))}

            {/* Services Dropdown */}
            {servicesNav.length > 0 && (
              <div className="relative" ref={servicesRef}>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className={cn(
                    'flex items-center gap-1 transition-colors whitespace-nowrap',
                    isActive('/services')
                      ? 'text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  )}
                >
                  {getLabel('common.services', 'الخدمات')}
                  <ChevronDown className={cn(
                    'w-4 h-4 transition-transform',
                    isServicesOpen && 'rotate-180'
                  )} />
                </button>

                {isServicesOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    {servicesNav.map((sub) => (
                      <Link
                        key={sub.path}
                        to={localizePath(sub.path)}
                        onClick={() => setIsServicesOpen(false)}
                        className={cn(
                          'block px-4 py-2 transition-colors',
                          isActive(sub.path)
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        )}
                      >
                        {getLabel(sub.labelKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Menu (only on small screens) */}
          <div className="md:hidden">
            <MobileMenu />
          </div>

          {/* Actions (يمين في RTL: لغة، إمكانية الوصول، بحث) - Figma */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => document.dispatchEvent(new CustomEvent('open-accessibility'))}
              className="p-2 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors"
              aria-label={t('accessibility.title', 'إعدادات إمكانية الوصول')}
            >
              <Accessibility className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors"
              aria-label={t('common.search')}
            >
              <Search className="w-5 h-5" />
            </button>
            {/* Dark mode toggle hidden on client website */}
            {/* <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              aria-label={t('brand.admin', 'لوحة التحكم')}
              title={t('brand.admin', 'لوحة التحكم')}
            >
              <Settings className="w-4 h-4" />
              <span>{t('brand.admin', 'لوحة التحكم')}</span>
            </Link> */}
          </div>
        </div>
      </div>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  )
}
